import React, { useState } from "react";
import { Row, Col, Badge, Modal } from "react-bootstrap";
import CommStatCards from "./CommStatCards";

const CATEGORY_STYLES = {
  Transactional: { bg: "#eff6ff", color: "#2563eb" },
  Promotional: { bg: "#f5f3ff", color: "#7c3aed" },
  Reminder: { bg: "#fffbeb", color: "#d97706" },
  Alert: { bg: "#fef2f2", color: "#dc2626" },
  Utility: { bg: "#ecfdf5", color: "#10b981" },
};

const catStyle = (cat) => CATEGORY_STYLES[cat] || CATEGORY_STYLES.Utility;

const statusVariant = (s) =>
  s === "Approved" ? "success" : s === "Rejected" ? "danger" : "warning";


const MEDIA_META = {
  image: { icon: "fal fa-image", label: "Image" },
  video: { icon: "fal fa-video", label: "Video" },
  document: { icon: "fal fa-file-alt", label: "Document" },
};

const fileName = (url) => {
  try {
    const clean = String(url).split("?")[0];
    return decodeURIComponent(clean.substring(clean.lastIndexOf("/") + 1)) || "Document";
  } catch (e) {
    return "Document";
  }
};

// Map a file extension to an icon / colour / label so a document header reads
// clearly as a clickable file (PDF, Word, Excel…).
const DOC_TYPES = {
  pdf: { icon: "fal fa-file-pdf", color: "#e23b3b", label: "PDF Document" },
  doc: { icon: "fal fa-file-word", color: "#2b579a", label: "Word Document" },
  docx: { icon: "fal fa-file-word", color: "#2b579a", label: "Word Document" },
  xls: { icon: "fal fa-file-excel", color: "#1d6f42", label: "Spreadsheet" },
  xlsx: { icon: "fal fa-file-excel", color: "#1d6f42", label: "Spreadsheet" },
  ppt: { icon: "fal fa-file-powerpoint", color: "#d24726", label: "Presentation" },
  pptx: { icon: "fal fa-file-powerpoint", color: "#d24726", label: "Presentation" },
};
const DOC_DEFAULT = { icon: "fal fa-file-alt", color: "#5b6b7b", label: "Document" };

const docMeta = (url, name) => {
  const src = String(name || url || "").split("?")[0];
  const m = src.match(/\.([a-z0-9]+)$/i);
  const ext = m ? m[1].toLowerCase() : "";
  return DOC_TYPES[ext] || DOC_DEFAULT;
};

// A document header rendered as a recognisable, clickable file card.
const DocumentCard = ({ url }) => {
  const name = url ? fileName(url) : "Document";
  const meta = docMeta(url, name);
  const inner = (
    <>
      <span className="comm-tpl-doc-thumb" style={{ background: `${meta.color}15`, color: meta.color }}>
        <i className={meta.icon} />
      </span>
      <span className="comm-tpl-doc-meta">
        <span className="comm-tpl-doc-name">{name}</span>
        <span className="comm-tpl-doc-type">{meta.label}{url ? " · Tap to open" : ""}</span>
      </span>
      {url && <i className="fal fa-download comm-tpl-doc-dl" />}
    </>
  );
  if (!url) return <div className="comm-tpl-doc-card">{inner}</div>;
  return (
    <a className="comm-tpl-doc-card" href={url} target="_blank" rel="noopener noreferrer">
      {inner}
    </a>
  );
};

// A clickable chip linking to the media — used as a fallback whenever inline
// rendering isn't possible (no URL, or the image/video failed to load).
const MediaChip = ({ media, meta }) => {
  const label = media.url ? `View ${meta.label.toLowerCase()}` : `${meta.label} attachment`;
  const className = `comm-tpl-media ${media.url ? "comm-tpl-media-doc" : "comm-tpl-media-placeholder"}`;
  if (!media.url) {
    return (
      <div className={className}>
        <i className={meta.icon} />
        <span className="comm-tpl-media-doc-name">{label}</span>
      </div>
    );
  }
  return (
    <a className={className} href={media.url} target="_blank" rel="noopener noreferrer">
      <i className={meta.icon} />
      <span className="comm-tpl-media-doc-name">{media.type === "document" ? fileName(media.url) : label}</span>
      <i className="fal fa-external-link-alt ms-auto" />
    </a>
  );
};

// Renders a template's header media (image / video / document). Falls back to a
// clickable chip when the URL is missing or the media fails to load.
const TemplateMedia = ({ media, height = 140 }) => {
  const [failed, setFailed] = useState(false);
  if (!media || !media.type) return null;
  const meta = MEDIA_META[media.type] || { icon: "fal fa-paperclip", label: "Attachment" };

  // Documents always render as a file card (clickable when a URL exists).
  if (media.type === "document") {
    return <DocumentCard url={media.url} />;
  }

  if (!media.url || failed) {
    return <MediaChip media={media} meta={meta} />;
  }

  if (media.type === "image") {
    return (
      <img
        className="comm-tpl-media-img"
        src={media.url}
        alt="Template header"
        style={{ maxHeight: height }}
        onError={() => setFailed(true)}
      />
    );
  }

  // video
  return (
    <video
      className="comm-tpl-media-img"
      src={media.url}
      controls
      style={{ maxHeight: height }}
      onError={() => setFailed(true)}
    />
  );
};

/**
 * Generic "Templates" sub-tab body — grid of template cards + detail modal.
 * Used by both WhatsApp and SMS template tabs.
 */
const TemplatesPanel = ({ channel, accentColor, templates }) => {
  const [selected, setSelected] = useState(null);
  // Active stat-card filter: "total" | "approved" | "pending" | "<category>".
  const [filter, setFilter] = useState("total");

  const approved = templates.filter((t) => t.status === "Approved").length;
  const pending = templates.length - approved;
  const categories = new Set(templates.map((t) => t.category)).size;

  const stats = [
    { key: "total", label: "Total Templates", value: templates.length, color: "#6366f1", icon: "fal fa-layer-group", sub: "All templates" },
    { key: "approved", label: "Approved", value: approved, color: "#10b981", icon: "fal fa-check-circle", sub: "Ready to use" },
    { key: "pending", label: "Pending", value: pending, color: "#d97706", icon: "fal fa-clock", sub: "Awaiting approval" },
    { key: "categories", label: "Categories", value: categories, color: "#0891b2", icon: "fal fa-tags", sub: "Distinct types" },
  ];

  // Click a card to filter; click the active card again to reset to all.
  const handleCardClick = (key) => {
    if (key === "categories") return; // count only — nothing single to filter
    setFilter((prev) => (prev === key ? "total" : key));
  };

  const filtered = templates.filter((t) => {
    if (filter === "approved") return t.status === "Approved";
    if (filter === "pending") return t.status !== "Approved";
    return true;
  });

  return (
    <div>
      <CommStatCards stats={stats} onCardClick={handleCardClick} activeKey={filter} />

      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div>
          <h6 className="fw-bold mb-0" style={{ color: "#212529" }}>
            {channel} Templates
            {filter !== "total" && (
              <span style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", marginLeft: 8 }}>
                · {filter === "approved" ? "Approved" : "Pending"}
              </span>
            )}
          </h6>
          <small className="text-muted">
            {filtered.length} template{filtered.length === 1 ? "" : "s"}
            {filter === "total" ? " available" : " shown"}
          </small>
        </div>
        {filter !== "total" && (
          <button
            type="button"
            className="btn btn-sm btn-light"
            style={{ fontSize: 12, borderRadius: 8 }}
            onClick={() => setFilter("total")}
          >
            <i className="fal fa-times me-1" /> Clear filter
          </button>
        )}
      </div>

      <Row className="g-3">
        {filtered.length === 0 ? (
          <Col xs={12}>
            <div className="text-center text-muted py-5" style={{ fontSize: 14 }}>
              No templates in this filter.
            </div>
          </Col>
        ) : (
          filtered.map((t) => {
          const cat = catStyle(t.category);
          return (
            <Col lg={4} md={6} key={t.id}>
              <div className="comm-template-card">
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <h6 className="fw-bold mb-0 comm-template-name" style={{ color: "#212529" }} title={t.name}>
                    {t.name}
                  </h6>
                  <div className="d-flex align-items-center" style={{ gap: 6, flexShrink: 0 }}>
                    <span
                      style={{
                        fontSize: "11px",
                        fontWeight: 600,
                        color: cat.color,
                        backgroundColor: cat.bg,
                        padding: "3px 10px",
                        borderRadius: "12px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {t.category}
                    </span>
                    {t.lang && <span className="comm-tpl-lang">{t.lang}</span>}
                  </div>
                </div>

                {/* Preview bubble — labelled Header / Body / Footer */}
                <div className="comm-template-bubble">
                  {(t.headerMedia || t.headerText) && (
                    <div className="comm-tpl-part">
                      {t.headerMedia && <TemplateMedia media={t.headerMedia} height={110} />}
                      {t.headerText && <p className="comm-template-header-text">{t.headerText}</p>}
                    </div>
                  )}
                  <div className="comm-tpl-part">
                    <p className="comm-template-body" title={t.body}>{t.body}</p>
                  </div>
                  {t.footer && (
                    <div className="comm-tpl-part">
                      <p className="comm-template-footer">{t.footer}</p>
                    </div>
                  )}
                </div>

                <div className="d-flex justify-content-between align-items-center mt-3">
                  <Badge pill variant={statusVariant(t.status)} style={{ fontWeight: 600, fontSize: "10px" }}>
                    {t.status}
                  </Badge>
                  <button type="button" className="comm-tpl-view-btn" onClick={() => setSelected(t)}>
                    <i className="fal fa-eye me-1" /> View
                  </button>
                </div>
              </div>
            </Col>
          );
          })
        )}
      </Row>

      {/* Detail modal — shows all template data */}
      <Modal show={!!selected} onHide={() => setSelected(null)} size="lg" centered dialogClassName="comm-tpl-modal">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 16, fontWeight: 700, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <i className="fab fa-whatsapp" style={{ color: accentColor }} />
            <span>{selected ? selected.name : ""}</span>
            {selected && (
              <span
                style={{
                  fontSize: "11px",
                  fontWeight: 600,
                  color: catStyle(selected.category).color,
                  backgroundColor: catStyle(selected.category).bg,
                  padding: "3px 10px",
                  borderRadius: "12px",
                }}
              >
                {selected.category}
              </span>
            )}
            {selected && (
              <Badge pill variant={statusVariant(selected.status)} style={{ fontWeight: 600, fontSize: "10px" }}>
                {selected.status}
              </Badge>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ overflow: "hidden" }}>
          {selected && (
            <>
              {/* Full message preview — only this content scrolls */}
              <div className="comm-tpl-modal-label">Message Preview</div>
              <div className="comm-template-bubble comm-tpl-modal-bubble" style={{ maxHeight: "55vh", overflowY: "auto" }}>
                {(selected.headerMedia || selected.headerText) && (
                  <div className="comm-tpl-part">
                    {selected.headerMedia && <TemplateMedia media={selected.headerMedia} height={260} />}
                    {selected.headerText && <p className="comm-template-header-text">{selected.headerText}</p>}
                  </div>
                )}
                <div className="comm-tpl-part">
                  <p className="comm-template-body" style={{ WebkitLineClamp: "unset", minHeight: 0 }}>
                    {selected.body}
                  </p>
                </div>
                {selected.footer && (
                  <div className="comm-tpl-part">
                    <p className="comm-template-footer">{selected.footer}</p>
                  </div>
                )}
                {selected.buttons && selected.buttons.length > 0 && (
                  <div className="comm-tpl-part">
                    <div className="comm-tpl-buttons">
                      {selected.buttons.map((b, i) => (
                        <span key={i} className="comm-tpl-button">
                          <i className={b.url ? "fal fa-external-link-alt" : b.phone_number ? "fal fa-phone" : "fal fa-reply"} />
                          {b.text || "Button"}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default TemplatesPanel;
